/**
 * Which parts of a finished reply fold into "Details". The rule: fold the
 * process, keep the outcome and anything that needs a person.
 *
 * Folded: every call that finished (including writes with an Undo, which stay
 * reachable inside Details and are counted on its label), reasoning,
 * "Explored N files" folds, a question that was since answered, and the
 * narration between tool calls. Kept: the final prose, approvals and
 * questions still open, failures, running work, anything with its own card,
 * and every write that returns an entity to open.
 */
import { entitiesFromToolOutput } from "@/features/agents/refs/toolEntities";
import type { AgentChange } from "@/features/agents/types";

import type { CaseAgentUIMessage } from "../useCaseAgentChat";
import { changeOfOutput } from "./messageParts";
import { toolNameOf, type AnyToolPart } from "./ToolCard";
import { rowStatusOf } from "./ToolRow";
import { toolKind } from "./toolTitles";

type Part = CaseAgentUIMessage["parts"][number];

export type DetailsSegment =
  | { kind: "single"; part: Part }
  | { kind: "group"; parts: AnyToolPart[] };

/**
 * A finished reply needs at least this many segments before Details is worth
 * a row, unless one of them is a run (the organizer, research...): its steps
 * and moves table are bulky enough to fold on their own.
 */
const MIN_FOLDED_SEGMENTS = 2;

const isToolPart = (p: { type: string }): p is AnyToolPart =>
  p.type === "dynamic-tool" || p.type.startsWith("tool-");

/** The tool calls of a message, in order. */
export const toolPartsOf = (parts: readonly Part[]): AnyToolPart[] =>
  parts.filter((p): p is AnyToolPart => isToolPart(p));

/**
 * A finished tool call that nobody needs to see: it succeeded, wrote nothing
 * with an Undo, returned nothing to open, and asked for no approval. A call
 * that refused for want of input folds once a later call of the same tool
 * went through (the question was answered).
 */
const isFoldableTool = (
  part: AnyToolPart,
  later: readonly AnyToolPart[]
): boolean => {
  const name = toolNameOf(part);
  const status = rowStatusOf(part);
  if (status === "needs")
    return later.some(
      (t) => toolNameOf(t) === name && rowStatusOf(t) === "done"
    );
  if (status !== "done") return false;
  const kind = toolKind(name);
  if (kind === "own") return false;
  if ("approval" in part && part.approval) return false;
  if (kind === "write" && entitiesFromToolOutput(part.output).length > 0)
    return false;
  return true;
};

export type SplitDetails<T> = {
  /** In their original order, for the Details block. */
  folded: T[];
  /** What stays in the transcript, in order. */
  shown: T[];
  /** Tool calls inside `folded`, for the label. */
  steps: number;
  /** Folded calls that changed something and can be undone, for the label. */
  changes: number;
};

const hasChange = (
  part: AnyToolPart,
  changesByCall: ReadonlyMap<string, AgentChange>
): boolean =>
  changesByCall.has(part.toolCallId) || Boolean(changeOfOutput(part.output));

/**
 * Splits one assistant chunk's segments into the Details block and what
 * stays visible. Does nothing (`folded` empty) unless enough would fold.
 * `allTools` is every tool call of the message, to tell an answered question
 * from an open one.
 */
export const splitDetails = <T extends DetailsSegment>(
  segments: readonly T[],
  allTools: readonly AnyToolPart[],
  changesByCall: ReadonlyMap<string, AgentChange>
): SplitDetails<T> => {
  const isToolSeg = (seg: T) =>
    seg.kind === "group" || isToolPart(seg.part as { type: string });
  const isTextSeg = (seg: T) =>
    seg.kind === "single" && seg.part.type === "text";

  // The reply's closing prose: the text after the last tool call that
  // precedes the last text. Earlier text is narration between calls.
  let lastText = -1;
  segments.forEach((seg, i) => {
    if (isTextSeg(seg)) lastText = i;
  });
  let lastToolBeforeText = -1;
  segments.forEach((seg, i) => {
    if (i < lastText && isToolSeg(seg)) lastToolBeforeText = i;
  });

  const laterTools = (part: AnyToolPart): AnyToolPart[] => {
    const at = allTools.findIndex((t) => t.toolCallId === part.toolCallId);
    return at < 0 ? [] : allTools.slice(at + 1);
  };

  const folded: T[] = [];
  const shown: T[] = [];
  let steps = 0;
  let changes = 0;
  segments.forEach((seg, i) => {
    let fold = false;
    if (seg.kind === "group") {
      fold = seg.parts.every((p) => rowStatusOf(p) === "done");
      if (fold) {
        steps += seg.parts.length;
        changes += seg.parts.filter((p) => hasChange(p, changesByCall)).length;
      }
    } else if (isToolPart(seg.part as { type: string })) {
      const part = seg.part as AnyToolPart;
      fold = isFoldableTool(part, laterTools(part));
      if (fold) {
        steps += 1;
        if (hasChange(part, changesByCall)) changes += 1;
      }
    } else if (seg.part.type === "reasoning") {
      fold = true;
    } else if (seg.part.type === "text") {
      fold = i <= lastToolBeforeText;
    }
    (fold ? folded : shown).push(seg);
  });

  const foldsARun = folded.some((seg) =>
    seg.kind === "group"
      ? seg.parts.some((p) => toolKind(toolNameOf(p)) === "run")
      : isToolPart(seg.part as { type: string }) &&
        toolKind(toolNameOf(seg.part as AnyToolPart)) === "run"
  );
  if ((folded.length < MIN_FOLDED_SEGMENTS && !foldsARun) || steps === 0)
    return { folded: [], shown: [...segments], steps: 0, changes: 0 };
  return { folded, shown, steps, changes };
};
